import {
  formatRelativeTime,
  normalizeReplyPermission,
  type PostDraft,
} from "@scroll/shared-utils";

export { formatRelativeTime };
export type { PostDraft };

const STORAGE_KEY = "scroll-post-drafts";
const MAX_DRAFTS = 20;
const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB base64 limit

function readDrafts(): PostDraft[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return (JSON.parse(raw) as PostDraft[]).map((draft) => ({
      ...draft,
      replyPermission: normalizeReplyPermission(draft.replyPermission),
    }));
  } catch {
    return [];
  }
}

function writeDrafts(drafts: PostDraft[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch {
    // localStorage full — drop oldest drafts and retry
    if (drafts.length > 1) {
      writeDrafts(drafts.slice(-Math.ceil(drafts.length / 2)));
    }
  }
}

export function getDrafts(): PostDraft[] {
  return readDrafts();
}

export function getDraftCount(): number {
  return readDrafts().length;
}

export function saveDraft(draft: Omit<PostDraft, "id" | "createdAt" | "updatedAt">, existingId?: string): void {
  const drafts = readDrafts();
  const now = new Date().toISOString();

  // Skip image if base64 is too large
  const imageDataUrl = draft.imageDataUrl && draft.imageDataUrl.length > MAX_IMAGE_SIZE
    ? null
    : draft.imageDataUrl;

  if (existingId) {
    const idx = drafts.findIndex((d) => d.id === existingId);
    if (idx !== -1) {
      drafts[idx] = {
        ...drafts[idx],
        ...draft,
        imageDataUrl,
        replyPermission: normalizeReplyPermission(draft.replyPermission),
        updatedAt: now,
      };
      writeDrafts(drafts);
      return;
    }
  }

  const newDraft: PostDraft = {
    id: crypto.randomUUID(),
    ...draft,
    imageDataUrl,
    replyPermission: normalizeReplyPermission(draft.replyPermission),
    createdAt: now,
    updatedAt: now,
  };

  drafts.push(newDraft);

  // FIFO eviction
  while (drafts.length > MAX_DRAFTS) {
    drafts.shift();
  }

  writeDrafts(drafts);
}

export function deleteDraft(id: string): void {
  const drafts = readDrafts().filter((d) => d.id !== id);
  writeDrafts(drafts);
}

export function clearDrafts(): void {
  localStorage.removeItem(STORAGE_KEY);
}
